/**
 * Web push notification types for Gravity Station.
 *
 * Subscriptions are identified by a unique ID (u128 as string),
 * not tied to any wallet address.
 */

/**
 * Auction notification preferences
 */
export interface AuctionNotificationPrefs {
  /** Notify of new auctions (all or above threshold) */
  notify_new_auctions: boolean;
  /** Notify when a new bid is placed on any auction */
  notify_new_bids: boolean;
  /** Only notify for auctions above this USD value in cents (null = all) */
  min_usd_threshold_cents: number | null;
}

/**
 * Governance notification preferences
 */
export interface GovernanceNotificationPrefs {
  /** Notify when new proposals are submitted */
  notify_new_proposals: boolean;
  /** Notify when proposals enter voting period */
  notify_voting_started: boolean;
  /** Notify when voting completes (includes result: Passed/Rejected/Failed) */
  notify_voting_complete: boolean;
}

/**
 * User notification preferences
 */
export interface NotificationPreferences {
  /** Master toggle for all notifications */
  enabled: boolean;
  /** Auction notification settings */
  auctions: AuctionNotificationPrefs;
  /** Governance notification settings */
  governance: GovernanceNotificationPrefs;
}

/**
 * Default notification preferences (all enabled)
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: true,
  auctions: {
    notify_new_auctions: true,
    notify_new_bids: true,
    min_usd_threshold_cents: null,
  },
  governance: {
    notify_new_proposals: true,
    notify_voting_started: true,
    notify_voting_complete: true,
  },
};

/**
 * Push subscription keys required for web push encryption
 */
export interface PushSubscriptionKeys {
  /** The p256dh key for encryption */
  p256dh: string;
  /** The auth secret */
  auth: string;
}

/**
 * Push subscription info sent to the backend
 * This is a stricter version of the browser's PushSubscriptionJSON
 */
export interface PushSubscriptionInfo {
  /** The push service endpoint URL */
  endpoint: string;
  /** Subscription keys for encryption */
  keys: PushSubscriptionKeys;
}

/**
 * Request to subscribe a browser to push notifications
 */
export interface SubscribeRequest {
  subscription: PushSubscriptionInfo;
  preferences?: NotificationPreferences;
}

/**
 * Response after successful subscription
 */
export interface SubscribeResponse {
  /** The unique subscription ID (must be stored by client) */
  subscription_id: string;
}

/**
 * Request to unsubscribe from push notifications
 */
export interface UnsubscribeRequest {
  subscription_id: string;
}

/**
 * Request to update notification preferences
 */
export interface UpdatePreferencesRequest {
  subscription_id: string;
  preferences: NotificationPreferences;
}

/**
 * Response for VAPID public key
 */
export interface VapidPublicKeyResponse {
  public_key: string;
}

/**
 * Response for getting preferences
 */
export interface PreferencesResponse {
  subscription_id: string;
  preferences: NotificationPreferences;
}

/**
 * Generic success response
 */
export interface SuccessResponse {
  success: boolean;
}

/**
 * Notification payload received by service worker
 */
export interface NotificationPayload {
  title: string;
  body: string;
  click_url: string;
  tag: string;
  icon?: string;
}
